/**
 * @file This is the my user service module
 * @copyright Andreas Horvath 2016-2024
 * @version 0.2.0
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, firstValueFrom } from 'rxjs';
import { UserInfoInterface } from '../../interfaces';
import { UserBackendService, AuthBackendService } from '../../backend-services';
import { Router } from '@angular/router';
import { MdbToastService } from '../mdb-toast';

@Injectable({
  providedIn: 'root',
})
export class MyUserService {
  private userSource: BehaviorSubject<UserInfoInterface> = new BehaviorSubject(
    UserInfoInterface.builder().build()
  );
  public user$: Observable<UserInfoInterface> = this.userSource.asObservable();
  private readonly resetSource = new Subject<void>();
  /**
   * Emits synchronously on every identity change (login, logout, guest login, server logout),
   * before the new session is announced. Services holding per-user data (coins, retention
   * status, user-targeted feature flags, …) clear it here so the next render never shows
   * the previous account's values while the new account's data loads.
   */
  public readonly reset$: Observable<void> = this.resetSource.asObservable();
  private sessionVersion = 0;
  private readonly CACHE_KEY = 'my_user_cache';
  private readonly CACHE_TTL = 60000; // 1 minute

  constructor(
    private userBackendService: UserBackendService,
    private authBackendService: AuthBackendService,
    private toast: MdbToastService,
    private router: Router
  ) {
    this.loadFromCache();
    this.update();
  }

  public async update() {
    const version = this.sessionVersion;
    try {
      const user = await this.userBackendService.getPlayerInfo();
      if (version !== this.sessionVersion) return;
      if (user.isLocked) {
        this.userGotLockedLogout(); // if user is locked, logout !!
        throw new Error('User is locked');
      }
      
      // Check if guest session has expired
      if (user.isGuest && user.guestExpiryDate) {
        const now = new Date().getTime();
        const expiry = new Date(user.guestExpiryDate).getTime();
        if (now > expiry) {
          this.guestSessionExpiredLogout();
          throw new Error('Guest session expired');
        }
      }
      
      this.userSource.next(user);
      this.saveToCache(user);
    } catch (e) {
      console.log('Could not get user info:', e);
    }
  }

  public async getUser(): Promise<UserInfoInterface> {
    return firstValueFrom(
      this.user$.pipe(filter((user) => !!user && !!user.userID))
    );
  }

  private async userGotLockedLogout() {
    try {
      // @TODO not working well, use authSerivce.logout() instead (BUT WHY NOT WORKING?)
      await this.authBackendService.logout();
      await this.router.navigate(['/'], {
        queryParams: { logout: true },
      });
      this.toast.warning(
        'Du wurdest ausgeloggt, weil dein Account gesperrt wurde.'
      );
    } catch (e) {
      console.warn('Could not logout:', e);
    }
  }

  private async guestSessionExpiredLogout() {
    try {
      await this.authBackendService.logout();
      await this.router.navigate(['/'], {
        queryParams: { logout: true },
      });
      this.toast.warning(
        'Deine Gastzeit ist abgelaufen. Bitte melde dich erneut an.'
      );
    } catch (e) {
      console.warn('Could not logout expired guest:', e);
    }
  }

  private loadFromCache(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (cached) {
        const { userData, expiry } = JSON.parse(cached);
        if (Date.now() < expiry && userData.userID) {
          this.userSource.next(userData);
        } else {
          localStorage.removeItem(this.CACHE_KEY);
        }
      }
    } catch (e) {
      console.warn('Could not load user from cache:', e);
      localStorage.removeItem(this.CACHE_KEY);
    }
  }

  private saveToCache(user: UserInfoInterface): void {
    try {
      const cacheData = {
        userData: user,
        expiry: Date.now() + this.CACHE_TTL
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      console.warn('Could not save user to cache:', e);
    }
  }

  /** Clear identity and invalidate user requests from the previous session. */
  public reset(): void {
    this.sessionVersion++;
    this.userSource.next(UserInfoInterface.builder().build());
    try {
      this.clearCache();
    } catch (error) {
      // Storage can be unavailable; the in-memory logout must still complete.
      console.warn('Could not clear user cache:', error);
    }
    this.resetSource.next();
  }

  public clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
  }
}
